import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import StoragePanel from "@/components/admin/StoragePanel";
import { Activity, Database, Globe, Sparkles, TrendingUp, RefreshCw, AlertTriangle, Loader2, Save, Pencil } from "lucide-react";

/**
 * Capacity health-check panel — the numbers that predict when the current
 * Emergent tier stops being enough.
 *
 * Metrics grouped by scaling axis:
 *   • Process (this container): RSS/VMS memory, CPU, host RAM %
 *   • MongoDB: data + storage + index size, collection & object counts
 *   • Network: site count, articles published/ready/pending, ingestion rate
 *   • AI spend: today, this month, % of monthly budget consumed
 *   • Growth projection: linear extrapolation to 69 sites
 *
 * Auto-refreshes every 15s while the page is open.
 */
export default function Capacity() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastFetch, setLastFetch] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const r = await api.get("/admin/capacity");
      setData(r.data);
      setLastFetch(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load capacity");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [load]);

  if (loading && !data) return <div className="p-8 text-zinc-400 text-sm">Loading capacity…</div>;
  if (error) return <div className="p-8 text-red-400 text-sm">Error: {error}</div>;
  if (!data) return null;

  return (
    <div className="max-w-6xl mx-auto p-8 space-y-8" data-testid="capacity-panel">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-100">
            Capacity & Growth
          </h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Leading indicators to watch as the network scales. Every metric maps to a
            specific bottleneck; the growth panel projects where you&apos;ll be at 69 sites.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-500 font-mono">
          {lastFetch && (
            <span>updated {lastFetch.toISOString().slice(11, 19)} UTC</span>
          )}
          <button
            onClick={load}
            data-testid="capacity-refresh-btn"
            className="p-2 border border-zinc-800 rounded-sm hover:border-amber-500 hover:text-amber-400 transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </header>

      {/* PROCESS */}
      <Section icon={Activity} title="Backend container" hint="Watch RSS memory as a proxy for tier ceiling — a single container typically caps around 512-2048 MB.">
        <MetricGrid>
          <Metric label="RSS memory" value={`${data.process.rss_mb} MB`} testid="capacity-rss" />
          <Metric label="CPU (sample)" value={`${data.process.cpu_pct}%`} testid="capacity-cpu" />
          <Metric label="Threads" value={data.process.num_threads} testid="capacity-threads" />
          {data.process.container_mem_used_mb != null && (
            <Metric
              label="Container memory"
              value={`${data.process.container_mem_used_mb} MB`}
              sub={
                data.process.container_mem_limit_mb
                  ? `of ${data.process.container_mem_limit_mb} MB limit${data.process.container_mem_used_pct != null ? ` · ${data.process.container_mem_used_pct}%` : ""}`
                  : "no hard limit set"
              }
              testid="capacity-container-mem"
              alert={data.process.container_mem_used_pct != null && data.process.container_mem_used_pct > 85}
            />
          )}
        </MetricGrid>
      </Section>

      {/* MONGO */}
      <Section icon={Database} title="MongoDB" hint="Cheap tier caps at ~10 GB stored data. Storage size grows with WiredTiger overhead — watch storage_size, not data_size.">
        {data.mongo.ok ? (
          <MetricGrid>
            <Metric label="Data size" value={`${data.mongo.data_size_mb} MB`} testid="capacity-mongo-data" />
            <Metric label="Storage size" value={`${data.mongo.storage_size_mb} MB`} sub="on-disk" testid="capacity-mongo-storage" />
            <Metric label="Index size" value={`${data.mongo.index_size_mb} MB`} testid="capacity-mongo-index" />
            <Metric label="Collections" value={data.mongo.collections} testid="capacity-mongo-colls" />
            <Metric label="Objects" value={data.mongo.objects.toLocaleString()} testid="capacity-mongo-objects" />
          </MetricGrid>
        ) : (
          <div className="text-xs text-amber-400">dbStats unavailable: {data.mongo.error}</div>
        )}
      </Section>

      <StoragePanel />

      {/* NETWORK */}
      <Section icon={Globe} title="Network scale" hint="Sites × articles-per-site × ingestion-crons drives everything downstream.">
        <MetricGrid>
          <Metric label="Sites total" value={data.network.sites_total} testid="capacity-sites" />
          <Metric label="Sites live" value={data.network.sites_live} testid="capacity-sites-live" />
          <Metric label="Articles published" value={data.network.articles_published.toLocaleString()} testid="capacity-arts-pub" />
          <Metric label="Awaiting moderation" value={data.network.articles_ready_or_pending} testid="capacity-arts-pending" />
          <Metric label="Ingested last 24h" value={data.network.articles_ingested_last_24h} testid="capacity-24h" />
          <Metric label="Avg articles / day" value={data.network.avg_articles_per_day} sub="7-day mean" testid="capacity-per-day" />
        </MetricGrid>
      </Section>

      {/* AI SPEND */}
      <Section icon={Sparkles} title="AI spend" hint="Scales linearly with articles rewritten. Set network-wide caps here — they gate every AI call across every site.">
        <AiBudgetEditor data={data} onSaved={load} />
        <div className="mt-6 pt-4 border-t border-zinc-800/60">
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Live spend</div>
          <MetricGrid>
            <Metric label="Today" value={`$${data.ai.spend_today_usd.toFixed(2)}`} testid="capacity-ai-today"
                    alert={data.ai.daily_budget_used_pct !== null && data.ai.daily_budget_used_pct > 80} />
            <Metric label="This month" value={`$${data.ai.spend_this_month_usd.toFixed(2)}`} testid="capacity-ai-month"
                    alert={data.ai.monthly_budget_used_pct !== null && data.ai.monthly_budget_used_pct > 80} />
            {data.ai.daily_budget_usd !== null && data.ai.daily_budget_usd !== undefined && (
              <Metric
                label="Daily used"
                value={`${data.ai.daily_budget_used_pct}%`}
                sub={`of $${data.ai.daily_budget_usd.toFixed(2)} cap`}
                testid="capacity-ai-daily-pct"
                alert={data.ai.daily_budget_used_pct > 80}
              />
            )}
            {data.ai.monthly_budget_usd && (
              <Metric
                label="Monthly used"
                value={`${data.ai.monthly_budget_used_pct}%`}
                sub={`of $${data.ai.monthly_budget_usd.toFixed(2)} cap`}
                testid="capacity-ai-pct"
                alert={data.ai.monthly_budget_used_pct > 80}
              />
            )}
          </MetricGrid>
        </div>
      </Section>

      {/* GROWTH PROJECTION */}
      <Section icon={TrendingUp} title="Growth projection to 69 sites" hint="Linear extrapolation from current per-site averages. Use these numbers when asking Emergent support about the right tier.">
        {data.growth.scale_factor_x ? (
          <>
            <div className="mb-4 text-xs text-zinc-500 font-mono">
              scale factor <span className="text-amber-400">{data.growth.scale_factor_x}×</span> ({data.network.sites_total} → 69 sites)
            </div>
            <MetricGrid>
              <Metric label="Projected articles total" value={data.growth.projected_articles_total.toLocaleString()} testid="capacity-proj-arts" />
              <Metric label="Projected articles / day" value={data.growth.projected_articles_per_day} testid="capacity-proj-per-day" />
              <Metric label="Projected AI / month" value={`$${data.growth.projected_ai_spend_per_month_usd.toFixed(2)}`} testid="capacity-proj-ai" />
              <Metric
                label="Projected DB size"
                value={data.growth.projected_mongo_data_size_gb ? `${data.growth.projected_mongo_data_size_gb} GB` : "—"}
                testid="capacity-proj-db"
              />
            </MetricGrid>
          </>
        ) : (
          <div className="text-xs text-zinc-500">No sites configured — projection unavailable.</div>
        )}
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, hint, children }) {
  return (
    <section className="border border-zinc-800 rounded-sm bg-zinc-950/30">
      <header className="flex items-center gap-2 px-5 py-3 border-b border-zinc-800">
        <Icon size={14} className="text-amber-500" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-100">{title}</h2>
      </header>
      {hint && <p className="text-xs text-zinc-500 px-5 py-3 border-b border-zinc-800 leading-relaxed">{hint}</p>}
      <div className="p-5">{children}</div>
    </section>
  );
}

function MetricGrid({ children }) {
  return <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{children}</div>;
}

function Metric({ label, value, sub, testid, alert }) {
  return (
    <div data-testid={testid} className="min-w-0">
      <div className={`text-xs uppercase tracking-wider mb-1 flex items-center gap-1 ${alert ? "text-red-400" : "text-zinc-500"}`}>
        {alert && <AlertTriangle size={11} />} {label}
      </div>
      <div className={`text-2xl font-display font-bold ${alert ? "text-red-400" : "text-zinc-100"} truncate`} title={String(value)}>
        {value}
      </div>
      {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

/**
 * Editable network-wide AI budget caps.
 *
 * Two dollar-level circuit breakers that gate ALL AI rewrites network-wide:
 *   - daily_cap_usd: bounds today's total spend across all sites
 *   - monthly_cap_usd: bounds this calendar month's total across all sites
 *
 * Empty or 0 disables that cap. Effective immediately on save — the next AI
 * call anywhere in the network raises 429 once a cap is crossed until the
 * counter resets or the operator raises the cap.
 */
function AiBudgetEditor({ data, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [daily, setDaily] = useState("");
  const [monthly, setMonthly] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const currentDaily = data.ai.daily_budget_usd;
  const currentMonthly = data.ai.monthly_budget_usd;

  function beginEdit() {
    setDaily(currentDaily !== null && currentDaily !== undefined ? String(currentDaily) : "");
    setMonthly(currentMonthly ? String(currentMonthly) : "");
    setEditing(true);
    setMessage("");
  }

  async function save() {
    setBusy(true); setMessage("");
    try {
      const body = {
        daily_cap_usd: daily === "" ? null : parseFloat(daily),
        monthly_cap_usd: monthly === "" ? null : parseFloat(monthly),
      };
      await api.put("/admin/ai-budget", body);
      setMessage("Caps saved. Every AI rewrite now respects the new limits.");
      setEditing(false);
      onSaved();
    } catch (e) {
      setMessage(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }

  if (!editing) {
    return (
      <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="ai-budget-panel">
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Network-wide caps</div>
          <div className="flex items-baseline gap-6 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Daily</div>
              <div className="font-display text-xl font-bold text-zinc-100" data-testid="ai-budget-daily-current">
                {currentDaily !== null && currentDaily !== undefined ? `$${currentDaily.toFixed(2)}` : <span className="text-zinc-500">uncapped</span>}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Monthly</div>
              <div className="font-display text-xl font-bold text-zinc-100" data-testid="ai-budget-monthly-current">
                {currentMonthly ? `$${currentMonthly.toFixed(2)}` : <span className="text-zinc-500">uncapped</span>}
              </div>
            </div>
          </div>
        </div>
        <button
          onClick={beginEdit}
          data-testid="ai-budget-edit-btn"
          className="inline-flex items-center gap-1 text-[11px] font-mono text-zinc-400 hover:text-white border border-zinc-700 hover:border-white rounded-sm px-2 py-1"
        >
          <Pencil size={11} /> Change caps
        </button>
        {message && (
          <div data-testid="ai-budget-message" className="w-full text-xs font-mono text-emerald-300 border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-2.5">
            {message}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="ai-budget-editor">
      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Network-wide caps</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Daily cap (USD)</span>
          <div className="flex items-center bg-zinc-950 border border-zinc-800 rounded-sm mt-1">
            <span className="pl-3 text-zinc-500 font-mono">$</span>
            <input
              type="number" min="0" step="0.01"
              value={daily}
              onChange={(e) => setDaily(e.target.value)}
              placeholder="empty = uncapped"
              data-testid="ai-budget-daily-input"
              disabled={busy}
              className="bg-transparent border-0 px-2 py-2 text-sm font-mono outline-none w-full disabled:opacity-50"
            />
          </div>
          <span className="text-[10px] text-zinc-500 mt-1 block">
            Blocks ALL AI calls when today&apos;s network spend reaches this. Resets at midnight.
          </span>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Monthly cap (USD)</span>
          <div className="flex items-center bg-zinc-950 border border-zinc-800 rounded-sm mt-1">
            <span className="pl-3 text-zinc-500 font-mono">$</span>
            <input
              type="number" min="0" step="1"
              value={monthly}
              onChange={(e) => setMonthly(e.target.value)}
              placeholder="empty = uncapped"
              data-testid="ai-budget-monthly-input"
              disabled={busy}
              className="bg-transparent border-0 px-2 py-2 text-sm font-mono outline-none w-full disabled:opacity-50"
            />
          </div>
          <span className="text-[10px] text-zinc-500 mt-1 block">
            Blocks ALL AI calls when this month&apos;s network spend reaches this. Resets on the 1st.
          </span>
        </label>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <button
          onClick={save}
          disabled={busy}
          data-testid="ai-budget-save-btn"
          className="inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 px-4 py-2 rounded-sm text-sm font-semibold"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save caps
        </button>
        <button
          onClick={() => { setEditing(false); setMessage(""); }}
          disabled={busy}
          className="text-zinc-400 hover:text-white px-3 py-2 text-sm"
        >
          Cancel
        </button>
      </div>
      {message && (
        <div data-testid="ai-budget-message" className="text-xs font-mono text-red-300 border border-red-500/30 bg-red-500/5 rounded-sm p-2.5">
          {message}
        </div>
      )}
    </div>
  );
}
